class RmcfHeroSlider extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.slides = [...this.querySelectorAll('.rmcf-hero-slide')];
    this.index = 0;
    this.paused = false;
    this.hovered = false;
    this.focused = false;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.hoverMedia = matchMedia('(hover: hover)');
    this.controls = this.querySelector('.rmcf-hero-controls');
    this.onClick = (event) => {
      const button = event.target.closest('[data-action]');
      if (!button) return;
      if (button.dataset.action === 'pause') {
        this.paused = !this.paused;
        button.textContent = this.paused ? 'Play' : 'Pause';
        button.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
      } else {
        this.show(this.index + (button.dataset.action === 'next' ? 1 : -1), true);
      }
      this.schedule();
    };
    this.onEnter = () => { this.hovered = this.hoverMedia.matches; this.schedule(); };
    this.onLeave = () => { this.hovered = false; this.schedule(); };
    this.onFocus = () => { this.focused = true; this.schedule(); };
    this.onBlur = (event) => { if (!this.contains(event.relatedTarget)) { this.focused = false; this.schedule(); } };
    this.onPointerDown = (event) => {
      this.suppressSwipeClickUntil = 0;
      if (event.pointerType !== 'touch') return;
      if (!event.isPrimary || this.swipe) { this.endSwipe(); return; }
      if (event.target.closest('.rmcf-hero-controls, button, input, select, textarea')) return;
      this.swipe = { id: event.pointerId, x: event.clientX, y: event.clientY, horizontal: false };
      this.schedule();
    };
    this.onPointerMove = (event) => {
      const swipe = this.swipe;
      if (!swipe || event.pointerId !== swipe.id) return;
      const dx = Math.abs(event.clientX - swipe.x);
      const dy = Math.abs(event.clientY - swipe.y);
      if (!swipe.horizontal && dy > 12 && dy > dx) { this.endSwipe(); return; }
      if (!swipe.horizontal && dx > 12 && dx > dy * 1.3) {
        swipe.horizontal = true;
        this.setPointerCapture(event.pointerId);
      }
    };
    this.onPointerUp = (event) => {
      const swipe = this.swipe;
      if (!swipe || event.pointerId !== swipe.id) return;
      const dx = event.clientX - swipe.x;
      const dy = event.clientY - swipe.y;
      const threshold = Math.max(40, this.clientWidth * 0.1);
      const change = swipe.horizontal && Math.abs(dx) >= threshold && Math.abs(dx) > Math.abs(dy) * 1.3;
      this.endSwipe();
      if (change) {
        this.suppressSwipeClickUntil = performance.now() + 500;
        this.show(this.index + (dx < 0 ? 1 : -1), true);
        this.schedule();
      }
    };
    this.onPointerCancel = (event) => {
      if (this.swipe?.id === event.pointerId) this.endSwipe();
    };
    this.onLostCapture = (event) => {
      // Ignore the child's implicit capture ending when we capture on the slider.
      if (event.target === this) this.onPointerCancel(event);
    };
    this.onSwipeClick = (event) => {
      // A completed drag over a hero link must not also activate that link.
      if (event.detail && performance.now() < this.suppressSwipeClickUntil) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.suppressSwipeClickUntil = 0;
      }
    };
    this.onVisibility = () => { if (document.hidden) this.endSwipe(); this.schedule(); };
    this.onMotion = () => this.schedule();
    this.addEventListener('click', this.onSwipeClick, true);
    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('pointermove', this.onPointerMove);
    this.addEventListener('pointerup', this.onPointerUp);
    this.addEventListener('pointercancel', this.onPointerCancel);
    this.addEventListener('lostpointercapture', this.onLostCapture);
    this.addEventListener('click', this.onClick);
    this.addEventListener('pointerenter', this.onEnter);
    this.addEventListener('pointerleave', this.onLeave);
    this.addEventListener('focusin', this.onFocus);
    this.addEventListener('focusout', this.onBlur);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.motion.addEventListener('change', this.onMotion);
    this.classList.add('is-ready');
    this.controls.hidden = false;
    this.show(0);
    this.schedule();
  }
  endSwipe() {
    const swipe = this.swipe;
    this.swipe = null;
    if (swipe && this.hasPointerCapture(swipe.id)) this.releasePointerCapture(swipe.id);
    this.schedule();
  }
  show(index, manual = false) {
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => {
      slide.hidden = false;
      slide.inert = i !== this.index;
      slide.setAttribute('aria-hidden', String(i !== this.index));
      slide.classList.toggle('is-active', i === this.index);
    });
    const status = this.querySelector('[data-status]');
    status.setAttribute('aria-live', manual ? 'polite' : 'off');
    status.textContent = `${this.index + 1} / ${this.slides.length}`;
  }
  schedule() {
    clearTimeout(this.timer);
    if (this.swipe || this.paused || this.hovered || this.focused || document.hidden || this.motion.matches || navigator.connection?.saveData) return;
    this.timer = setTimeout(() => { this.show(this.index + 1); this.schedule(); }, Number(this.dataset.interval) || 7000);
  }
  disconnectedCallback() {
    this.endSwipe();
    this.suppressSwipeClickUntil = 0;
    clearTimeout(this.timer);
    this.removeEventListener('click', this.onSwipeClick, true);
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('pointermove', this.onPointerMove);
    this.removeEventListener('pointerup', this.onPointerUp);
    this.removeEventListener('pointercancel', this.onPointerCancel);
    this.removeEventListener('lostpointercapture', this.onLostCapture);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('pointerenter', this.onEnter);
    this.removeEventListener('pointerleave', this.onLeave);
    this.removeEventListener('focusin', this.onFocus);
    this.removeEventListener('focusout', this.onBlur);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.motion?.removeEventListener('change', this.onMotion);
    this.ready = false;
  }
}
if (!customElements.get('rmcf-hero-slider')) customElements.define('rmcf-hero-slider', RmcfHeroSlider);
